import React, { useState } from "react";
import { formatYmd } from "../../utils/dateOnly";
import { SignatureCreator } from "../../pages/digi/NewMasterSheet/LeaseProcess/Flow/StepperComponents/lease/components/steps/components/SignatureService/SignatureCreator";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Check = ({ on }) => (
  <span aria-hidden="true" style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 16, height: 16, border: "1.5px solid #0c447c", borderRadius: 3,
    color: "#0c447c", fontSize: 12, lineHeight: 1, marginRight: 8, flex: "0 0 auto",
  }}>
    {on ? "✓" : ""}
  </span>
);

const HeaderRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: 10, padding: "4px 0", fontSize: 14 }}>
    <span style={{ minWidth: 210, color: "#555" }}>{label}</span>
    <span style={{ flex: 1, fontWeight: 600, borderBottom: "1px dotted #c8cdd3" }}>{value || " "}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: 14, fontWeight: 700, color: "#111827",
    borderBottom: "1px solid #d1d5db", paddingBottom: 6, margin: "24px 0 14px",
  }}>
    {children}
  </div>
);

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

const composeAddress = (a) => {
  const unit = String(a.unit_no || "").trim();
  const bld = String(a.building_name || "").trim();
  if (unit || bld) return [unit, bld].filter(Boolean).join(" - ");
  return a.property_address || "";
};

/** One generic amendment, worded plainly for a legal document. */
const AmendmentLine = ({ item, showEffective, fallbackEffectiveDate }) => {
  const name = item.display_name || item.label || item.field_key;
  const effectiveDate = item.effective_date || fallbackEffectiveDate;
  const effectiveLine =
    showEffective && effectiveDate ? (
      <div style={{ paddingLeft: 18 }}>
        Effective date: <strong>{formatYmd(effectiveDate)}</strong>
      </div>
    ) : null;
  if (item.type === "text") {
    return (
      <div style={{ margin: "7px 0" }}>
        • <strong>{name}:</strong> {item.new_value || "—"}
        {effectiveLine}
      </div>
    );
  }
  const o = num(item.old_value);
  const n = num(item.new_value);
  const action = item.action || (n === 0 && o > 0 ? "removed" : !o && n > 0 ? "added" : "updated");
  const oneTime = !!item.one_time || item.field_key === "psfNonRefundable";
  const hasVal = (v) => v !== undefined && v !== null && v !== "";
  const hasOldQty = hasVal(item.old_quantity);
  const hasNewQty = hasVal(item.quantity);
  const per = oneTime ? " (one-time charge)" : " per month";
  return (
    <div style={{ margin: "10px 0" }}>
      • <strong>{name}</strong>
      {hasOldQty && hasNewQty && item.old_quantity !== item.quantity ? (
        <> — Quantity: <strong>{item.old_quantity} → {item.quantity}</strong></>
      ) : hasNewQty || hasOldQty ? (
        <> — Quantity: <strong>{hasNewQty ? item.quantity : item.old_quantity}</strong></>
      ) : null}
      <div style={{ paddingLeft: 18, color: "#4b5563" }}>
        Current charge: {action === "added" ? "Not currently charged" : `${money(o)}${per}`}
      </div>
      <div style={{ paddingLeft: 18 }}>
        New charge under this addendum: {action === "removed" ? (
          <strong>Removed — no longer charged</strong>
        ) : (
          <strong>{money(n)}{per}</strong>
        )}
      </div>
      {effectiveLine}
      {oneTime && (
        <div style={{ paddingLeft: 18, color: "#6b7280", fontSize: 12.5 }}>
          One-time charge — not included in the monthly rent.
        </div>
      )}
    </div>
  );
};

const RentLine = ({ label, value, strong }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #eef0f2", fontSize: 14, fontWeight: strong ? 700 : 400 }}>
    <span>{label}</span>
    <span>{money(value)}</span>
  </div>
);

/**
 * Individual signature line. In sign mode it shows a tappable area that opens
 * the SignatureCreator dialog (Type / Draw / Upload). Each signer's link is
 * unique so only their line is interactive — matching the lease module pattern.
 */
const SignatureLine = ({ role, name, date, image, signMode, signatureValue, onSign, capturedDate, disabled }) => {
  const [open, setOpen] = useState(false);

  const handleCreated = ({ signatureData }) => {
    if (signatureData && onSign) onSign(signatureData);
  };

  return (
    <>
      {signMode && (
        <SignatureCreator
          isOpen={open}
          onClose={() => setOpen(false)}
          onSign={handleCreated}
          disableSignAndSave
        />
      )}

      <div style={{ flex: "1 1 240px", minWidth: 220 }}>
        {/* Sign-mode tap area */}
        {signMode ? (
          <div
            onClick={() => { if (!disabled) setOpen(true); }}
            style={{
              minHeight: 72, borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer",
              border: signatureValue ? "2px solid #0f6e56" : disabled ? "2px dashed #cbd5e1" : "2px dashed #a5b4fc",
              background: signatureValue ? "#f0faf5" : disabled ? "#f8fafc" : "#f5f7ff",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 6, padding: "8px 12px",
              boxShadow: signatureValue
                ? "0 0 0 3px rgba(15,110,86,0.1)"
                : disabled
                ? "none"
                : "0 0 0 3px rgba(99,102,241,0.08)",
              transition: "all 0.2s",
            }}
          >
            {signatureValue ? (
              <img src={signatureValue} alt="Your signature" style={{ maxHeight: 56, maxWidth: "100%", display: "block" }} />
            ) : (
              <div style={{ textAlign: "center", pointerEvents: "none" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>✍</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: disabled ? "#94a3b8" : "#4f46e5" }}>
                  {disabled ? "Tick the agreement box above to sign" : "Tap to sign"}
                </div>
                {!disabled && <div style={{ fontSize: 11, color: "#818cf8", marginTop: 2 }}>Type, draw, or upload</div>}
              </div>
            )}
          </div>
        ) : (
          /* Read-only signature display */
          <div style={{ height: 56, display: "flex", alignItems: "flex-end", marginBottom: 6 }}>
            {image ? (
              <img src={image} alt="signature" style={{ maxHeight: 52, maxWidth: 220 }} />
            ) : date ? (
              <span style={{ color: "#0f6e56", fontSize: 12 }}>✓ Signed electronically</span>
            ) : (
              <span style={{ color: "#aaa", fontSize: 12 }}>Awaiting signature</span>
            )}
          </div>
        )}

        {/* Name / date line */}
        <div style={{ borderTop: "1px solid #333", paddingTop: 4, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>{name || " "}</span>
          <span style={{ color: "#666" }}>
            {signMode && signatureValue && capturedDate
              ? `Date: ${capturedDate}`
              : date
              ? `Date: ${formatYmd(date)}`
              : "Date: ____________"}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{role}</div>

        {/* Re-draw link once signed */}
        {signMode && signatureValue && (
          <button
            onClick={() => { if (!disabled) setOpen(true); }}
            style={{
              marginTop: 6, fontSize: 11, color: "#4f46e5", background: "none",
              border: "none", cursor: "pointer", padding: 0, textDecoration: "underline",
            }}
          >
            Re-draw signature
          </button>
        )}
      </div>
    </>
  );
};

/* ─── Main document component ────────────────────────────────────────────── */
const AddendumDocument = ({ addendum = {}, signTenantIndex = null, signatureValue = "", onSign, capturedDate = "", fluid = false, agreed = false, onAgreedChange }) => {
  const ad = addendum.amendment_details || {};
  const rr = addendum.revised_rent || {};
  const tenantSigs = addendum.tenant_signatures || [];
  const landlord = addendum.landlord_signature || {};
  const amendments = Array.isArray(addendum.amendments) ? addendum.amendments : [];
  const hasGeneric = amendments.length > 0;
  // Every amendment may carry its own start date; the addendum-level date only
  // stands in for addendums saved before per-item dates existed.
  const perItemDates = amendments.some((it) => it.effective_date);
  const totalValue = rr.new_total_monthly_rent;
  const signMode = signTenantIndex != null;
  const anyTenantSigned = tenantSigs.some((t) => t.signed);

  // `fluid` fills the parent container (the tenant page wraps it in its own card);
  // otherwise the document renders as its own centered card (manager preview).
  const rootStyle = fluid
    ? {
        width: "100%", background: "transparent", border: "none", borderRadius: 0,
        boxShadow: "none", padding: 0,
        fontFamily: "-apple-system, 'Segoe UI', Arial, Helvetica, sans-serif", color: "#1f2937",
      }
    : {
        maxWidth: 760, margin: "0 auto", background: "#fff", border: "1px solid #e6e9ee",
        borderRadius: 10, boxShadow: "0 1px 4px rgba(16,42,80,0.06)", padding: "40px 48px",
        fontFamily: "-apple-system, 'Segoe UI', Arial, Helvetica, sans-serif", color: "#1f2937",
      };

  return (
    <div className="addendum-doc" style={rootStyle}>
      <style>{`.addendum-doc strong, .addendum-doc b { font-size: inherit; font-weight: 700; color: inherit; line-height: inherit; }`}</style>
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <h1 style={{ fontSize: 23, fontWeight: 700, margin: 0 }}>Lease Addendum Agreement</h1>
      </div>
      <div style={{ borderTop: "2px solid #111827", margin: "12px 0 20px" }} />

      <HeaderRow label="Property Address:" value={composeAddress(addendum)} />
      <HeaderRow label="Tenant(s):" value={addendum.tenant_names} />
      <HeaderRow label="Original Lease Date:" value={addendum.original_lease_date ? formatYmd(addendum.original_lease_date) : ""} />
      {addendum.lease_end_date && <HeaderRow label="Lease End Date:" value={formatYmd(addendum.lease_end_date)} />}
      <HeaderRow
        label="Effective Date of Amendment:"
        value={
          perItemDates
            ? `As listed per amendment below${addendum.effective_date ? ` (starting ${formatYmd(addendum.effective_date)})` : ""}`
            : addendum.effective_date
            ? formatYmd(addendum.effective_date)
            : ""
        }
      />

      <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "#374151", marginTop: 18 }}>
        This Addendum forms part of and amends the Residential Tenancy Agreement for the above-noted premises. Except as
        amended herein, all other terms and conditions of the original lease agreement remain unchanged and in full force and effect.
      </p>

      <SectionTitle>Amendment Details</SectionTitle>
      {hasGeneric ? (
        <div style={{ fontSize: 14, lineHeight: 1.7 }}>
          {amendments.map((it, i) => (
            <AmendmentLine
              key={i}
              item={it}
              showEffective={perItemDates}
              fallbackEffectiveDate={addendum.effective_date}
            />
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, lineHeight: 1.8 }}>
          <div style={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
            <Check on={!!ad.parking?.selected} /> Parking
          </div>
          {ad.parking?.selected && (
            <div style={{ paddingLeft: 24, color: "#333" }}>
              New Monthly Parking Charge: <strong>{money(ad.parking.new_monthly_charge)}</strong>
              {ad.parking.action ? ` (${ad.parking.action})` : ""}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", fontWeight: 600, marginTop: 8 }}>
            <Check on={!!ad.pet?.selected} /> Pet Fee
          </div>
          {ad.pet?.selected && (
            <div style={{ paddingLeft: 24, color: "#333" }}>
              Pet Description: <strong>{ad.pet.description || "—"}</strong>
              {ad.pet.action ? ` (${ad.pet.action})` : ""}<br />
              Non-Refundable Pet Fee: <strong>{money(ad.pet.non_refundable_fee)}</strong><br />
              New Monthly Pet Fee: <strong>{money(ad.pet.new_monthly_fee)}</strong>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", fontWeight: 600, marginTop: 8 }}>
            <Check on={(ad.others || []).length > 0} /> Other Amendment
          </div>
          {(ad.others || []).length > 0 && (
            <div style={{ paddingLeft: 24, color: "#333" }}>
              {ad.others.map((o, i) => (
                <div key={i}>{o.label || "Other"}: <strong>{money(o.amount)}</strong></div>
              ))}
            </div>
          )}
        </div>
      )}

      {hasGeneric ? (
        totalValue != null && num(totalValue) > 0 && (
          <>
            <SectionTitle>Revised Monthly Rent</SectionTitle>
            <div style={{
              padding: "11px 14px", background: "#f3f5f8", borderRadius: 6,
              display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, color: "#111827",
            }}>
              <span>New Total Monthly Rent Payable</span>
              <span>{money(totalValue)}</span>
            </div>
          </>
        )
      ) : (
        <>
          <SectionTitle>Revised Monthly Rent</SectionTitle>
          <div>
            <RentLine label="New Base Rent" value={rr.new_base_rent} />
            <RentLine label="New Parking Charge" value={rr.new_parking_charge} />
            <RentLine label="New Pet Fee" value={rr.new_pet_fee} />
            <RentLine label="New Storage Fee" value={rr.new_storage_fee} />
            <RentLine label="New Other Charges" value={rr.new_other_charges} />
            <div style={{
              marginTop: 6, padding: "10px 12px", background: "#f3f5f8", borderRadius: 6,
              display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, color: "#111827",
            }}>
              <span>New Total Monthly Rent Payable</span>
              <span>{money(rr.new_total_monthly_rent)}</span>
            </div>
          </div>
        </>
      )}

      <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "#374151", marginTop: 22 }}>
        The Tenant(s) and Landlord acknowledge and agree to the above amendment. This Addendum shall become part of the
        existing Residential Tenancy Agreement effective on the {perItemDates ? "date noted against each amendment" : "date noted"} above.
      </p>

      <div style={{
        display: "flex", alignItems: "flex-start", gap: 10, margin: "18px 0 4px",
        padding: signMode ? "14px 16px" : "4px 0",
        background: signMode ? "#F5F7FF" : "transparent",
        border: signMode ? "1px solid #C7D2FE" : "none", borderRadius: 10,
      }}>
        {signMode ? (
          <input
            id="addendum-agree"
            type="checkbox"
            checked={!!agreed}
            onChange={(e) => onAgreedChange && onAgreedChange(e.target.checked)}
            style={{ width: 18, height: 18, marginTop: 2, cursor: "pointer", flex: "0 0 auto" }}
          />
        ) : (
          <Check on={anyTenantSigned} />
        )}
        <label
          htmlFor={signMode ? "addendum-agree" : undefined}
          style={{ fontSize: 13.5, lineHeight: 1.6, color: "#1f2937", cursor: signMode ? "pointer" : "default", fontWeight: signMode ? 600 : 400, margin: 0 }}
        >
          I have checked everything in this addendum in detail and agree to pay the amounts mentioned in it.
        </label>
      </div>

      <SectionTitle>Signatures</SectionTitle>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginTop: 8 }}>
        {tenantSigs.map((t) => (
          <SignatureLine
            key={t.tenant_index}
            role="Tenant Signature"
            name={t.name}
            date={t.date}
            image={t.signature}
            signMode={signTenantIndex === t.tenant_index}
            signatureValue={signatureValue}
            onSign={onSign}
            capturedDate={capturedDate}
            disabled={signTenantIndex === t.tenant_index && !agreed}
          />
        ))}
        <SignatureLine
          role="Landlord / Property Manager"
          name={landlord.name}
          date={landlord.date}
          image={landlord.signature}
        />
      </div>
    </div>
  );
};

export default AddendumDocument;
